import React, { useEffect, useState } from "react";
import { Instagram, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, brand } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import BookCta from "@/components/BookCta";

const CATEGORIES = ["All", "Animals", "Fantasy", "Superheroes and Games", "Flowers and Nature", "Birthday Parties", "Markets and Events"];

export default function Gallery() {
  useSeo({ title: "Gallery — Face Painting Designs", description: "Browse Paint With Tay's face painting designs: animals, fantasy, superheroes, flowers, birthdays and market events in Nanaimo." });
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery", { params: cat === "All" ? {} : { category: cat } }).then((r) => setItems(r.data || [])).catch(() => {});
  }, [cat]);

  const openAt = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const prev = (e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + items.length) % items.length); };
  const next = (e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % items.length); };

  return (
    <div data-testid="gallery-page">
      <section className="section pt-14 pb-6 text-center">
        <Reveal>
          <p className="font-accent text-3xl text-hotpink">See the magic</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-700 mb-3">The Paint With Tay Gallery</h1>
          <p className="max-w-2xl mx-auto text-lg text-ink/80 mb-5">A peek at the colourful faces Tay loves to create — from sparkling butterflies and galaxy fairies to fierce little tigers and wild adventures.</p>
          <a href={brand.instagram} target="_blank" rel="noopener noreferrer" className="btn-pink" data-testid="gallery-instagram">
            <Instagram className="w-5 h-5" /> Follow @paint.with.tay
          </a>
        </Reveal>
      </section>

      <section className="section pb-4">
        <div className="flex flex-wrap gap-2 justify-center" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full border-[2.5px] border-ink font-heading font-500 transition ${cat === c ? "bg-hotpink text-white" : "bg-white text-ink hover:bg-sunshine"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="section pb-14">
        {items.length === 0 ? (
          <p className="text-center text-ink/60 py-10">No photos in this category yet — check back soon!</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {items.map((it, i) => (
              <Reveal key={it.id} delay={(i % 3) * 0.05}>
                <button
                  onClick={() => openAt(i)}
                  data-testid={`gallery-item-${i}`}
                  className="mb-5 block w-full text-left card-soft overflow-hidden p-0"
                  style={{ transform: `rotate(${(i % 3) - 1}deg)` }}
                >
                  <img src={it.url} alt={it.alt || it.caption} loading="lazy" className="w-full object-cover" />
                  <div className="p-3">
                    <span className="chip text-xs mb-1">{it.category}</span>
                    <p className="font-heading text-lg mt-1">{it.caption}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {lightbox !== null && items[lightbox] && (
        <div className="fixed inset-0 z-[100] bg-ink/90 flex items-center justify-center p-4" onClick={close} data-testid="lightbox">
          <button onClick={close} className="absolute top-5 right-5 text-white p-2" aria-label="Close" data-testid="lightbox-close"><X className="w-8 h-8" /></button>
          <button onClick={prev} className="absolute left-3 sm:left-8 text-white p-2" aria-label="Previous"><ChevronLeft className="w-10 h-10" /></button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={items[lightbox].url} alt={items[lightbox].alt || items[lightbox].caption} className="w-full max-h-[75vh] object-contain rounded-2xl border-[3px] border-cream" />
            <p className="text-cream text-center mt-3 font-heading text-xl">{items[lightbox].caption}</p>
          </div>
          <button onClick={next} className="absolute right-3 sm:right-8 text-white p-2" aria-label="Next"><ChevronRight className="w-10 h-10" /></button>
        </div>
      )}

      <BookCta />
    </div>
  );
}
